import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../../components/button";
import FitnessToolsResultCard from "../../../components/cards/FitnessToolsResultCard";
import Footer from "../../../components/footer";
import Header from "../../../components/header";
import Input from "../../../components/input";
import OtherCalculatorSection from "../../../components/OtherCalculatorSection";
import AISkeleton from "../../../components/skeleton/AISkeleton";
import { useTranslate } from "../../../context/TranslationContext";
import useChartPeriod from "../../../hooks/useChartPeriod";
import {
  useCreateSleepMutation,
  useGetChartQuery,
  useGetSleepQuery,
  useUpdateSleepMutation,
} from "../../../redux/Apis/Calculator/Sleep";
import { useCreateAISuggestionMutation } from "../../../redux/Apis/TrackLecture";
import { useStaticData } from "../../../utils/data";
import { formatAISuggestion, setMetaTags } from "../../../utils/helper";

const Sleep = () => {
  const { t } = useTranslate("sleep");
  const lang = useSelector((state) => state.language.lang);
  const { user } = useSelector((state) => state.authSlice);
  const { CALCULATORS, CALCULATOR_DATA } = useStaticData();
  const navigate = useNavigate();
  const location = useLocation();
  const [fatigue, setFatigue] = useState("low");
  const [stress, setStress] = useState("low");
  const [sleepQuality, setSleepQuality] = useState("good");
  const { period, dateRange, handlePeriodChange } = useChartPeriod();
  const { startDate, endDate } = dateRange;
  const [result, setResult] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [aiSuggestion, setAiSuggestion] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [highlightAI, setHighlightAI] = useState(false);
  const aiRef = useRef(null);
  const [createAISuggestion] = useCreateAISuggestionMutation();
  const [createSleep] = useCreateSleepMutation();
  const [updateSleep] = useUpdateSleepMutation();
  const { data: sleepHistoryData, refetch: refetchSleep } = useGetSleepQuery(
    { lang },
    { skip: !user },
  );
  const { data: SleepCartData, refetch: refetchSleepChartData } =
    useGetChartQuery(
      { startdate: startDate, enddate: endDate, lang },
      { skip: !startDate || !endDate || !user },
    );
  const courseState = location?.state || {};
  // const templateId = courseState?.templateId;
  const courseId = courseState?.course;
  const sectionId = courseState?.sectionId || courseState?.section;
  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    if (aiLoading) {
      const timer = setTimeout(() => setShowSkeleton(true), 200);
      return () => clearTimeout(timer);
    } else {
      setShowSkeleton(false);
    }
  }, [aiLoading]);

  useEffect(() => {
    if ((aiLoading || aiSuggestion) && aiRef.current) {
      setTimeout(() => {
        const y =
          aiRef.current.getBoundingClientRect().top + window.pageYOffset - 110; // header height offset

        window.scrollTo({
          top: y,
          behavior: "smooth",
        });

        setHighlightAI(true);
        setTimeout(() => setHighlightAI(false), 3000);
      }, 300);
    }
  }, [aiLoading, aiSuggestion]);

  useEffect(() => {
    if (user) {
      refetchSleep();
    }
  }, [user, refetchSleep]);

  useEffect(() => {
    if (startDate && endDate && user) {
      refetchSleepChartData();
    }
  }, [startDate, endDate, user, refetchSleepChartData]);

  useEffect(() => {
    setMetaTags(
      "Traineego | Buy or Create Automated Courses & Tracks for Fitness & Wellness",
      "Create, manage, and sell your own courses and tracks, or buy high-quality content. Empower your career through automation and passive income today!",
    );
  }, []);

  useEffect(() => {
    if (sleepHistoryData) {
      setHistoryData(sleepHistoryData.data || []);
    }
  }, [sleepHistoryData]);

  useEffect(() => {
    if (startDate && endDate && user) {
      refetchSleepChartData();
    }
  }, [startDate, endDate, user, refetchSleepChartData]);

  const calculateSleep = () => {
    let recommendation = "7–7.5";

    const isHighFatigue = fatigue === "high";
    const isHighStress = stress === "high";
    const isMediumFatigue = fatigue === "medium";
    const isMediumStress = stress === "medium";

    const highCount = [isHighFatigue, isHighStress].filter(Boolean).length;
    const hasMedium = isMediumFatigue || isMediumStress;

    // 1. Highest demand (2 highs)
    if (highCount === 2) {
      recommendation = "9.5–10";
    }

    // 2. One high
    else if (highCount === 1) {
      if (sleepQuality === "poor") {
        recommendation = "9.5–10"; // escalation
      } else {
        recommendation = "9–9.5";
      }
    }

    // 3. Medium cases
    else if (hasMedium) {
      if (sleepQuality === "poor") {
        recommendation = "9–9.5"; // escalation
      } else {
        recommendation = "8–9";
      }
    }

    // 4. No high/medium (LOW cases)
    else {
      if (sleepQuality === "average") {
        recommendation = "8–9"; // baseline upgrade
      } else if (sleepQuality === "poor") {
        recommendation = "9–9.5"; // escalation even for low-low-poor
      } else {
        recommendation = "7–7.5";
      }
    }

    setResult(recommendation);
    toast.success(t("toasts.calculateSuccess"));

    return recommendation;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return t("history.notCalculated");
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatHistoryEntry = (entry) => {
    const createdAt = new Date(entry.createdAt).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return (
      <div className="d-flex justify-content-between align-items-center">
        <p className="mb-0">{createdAt}</p>
        <p className="mb-0 fw-bold">
          {entry.result} {t("hours")}
        </p>
      </div>
    );
  };

  const generateAICoach = async (inputResult) => {
    try {
      // if (!courseId || !sectionId) return;

      setAiLoading(true);

      const aiPayload = {
        courseid: courseId,
        sectionid: sectionId,
        learnerid: user._id,
        type: "Sleep",
        result: [
          {
            fatigue_level: inputResult.fatigue_level,
            stress_level: inputResult.stress_level,
            sleep_quality: inputResult.sleep_quality,
            result: { result: inputResult.result, unit: "hours" },
          },
        ],
      };

      const res = await createAISuggestion({
        payload: aiPayload,
        params: { lang },
      }).unwrap();

      const suggestion = res?.data?.suggestion;
      if (suggestion) setAiSuggestion(suggestion);
    } catch (err) {
      console.log("AI error", err);
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveResult = async (type, result) => {
    if (!result) {
      toast.error(t("toasts.calculateFirst"));
      return;
    }

    if (!user) {
      toast.error(t("toasts.loginRequired"));
      // navigate("/auth/login");
      return;
    }

    try {
      const payload = {
        fatigue_level: fatigue,
        stress_level: stress,
        sleep_quality: sleepQuality,
        result: result.result,
      };
      if (courseId) payload.course = courseId;
      const response = await createSleep({
        payload,
        params: { lang },
      }).unwrap();
      toast.success(response.message || t("toasts.saveSuccess"));
      refetchSleep();
      refetchSleepChartData();
      // if (courseId && sectionId && templateId) {
      await generateAICoach(payload);
      // }
    } catch (error) {
      console.error("Error saving Sleep:", error);
      toast.error(t("toasts.saveFail"));
    }
  };

  const handleDeleteLast = async () => {
    try {
      const latestEntry = [...historyData].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      )[0];
      if (latestEntry) {
        const payload = {
          id: latestEntry._id,
          isactive: false,
          params: { lang },
        };
        await updateSleep(payload).unwrap();
        toast.success(t("toasts.deleteSuccess"));
        refetchSleep();
        refetchSleepChartData();
      }
    } catch (error) {
      console.error("Error deleting last Sleep:", error);
      toast.error(t("toasts.deleteFail"));
    }
  };

  const toggleHistory = () => {
    setShowHistory((prev) => !prev);
  };

  return (
    <>
      <Header />
      <section className="banner" style={{ textAlign: "left" }}>
        <div className="container py-5">
          <div className="row">
            <div className="col-12 text-center">
              <h1 className="text-uppercase">{t("title")}</h1>
              <p className="mb-0 p-lg l-grey-text">{t("subtitle")}</p>
            </div>
          </div>

          <div className="row d-flex">
            <div
              className="col-md-12 mt-4 p-5"
              style={{ backgroundColor: "#161617", borderRadius: "2rem" }}
            >
              <div className="row">
                <div className="col-lg-8">
                  <h4 className="text-uppercase mb-4">
                    {t("calculateSectionTitle")}
                  </h4>

                  <div className="row">
                    <div className="col-md-6">
                      <Input
                        label={t("inputs.fatigue")}
                        type="select"
                        options={CALCULATOR_DATA.LEVEL}
                        value={fatigue}
                        onChange={(e) => setFatigue(e.target.value)}
                        required={true}
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label={t("inputs.stress")}
                        type="select"
                        options={CALCULATOR_DATA.LEVEL}
                        value={stress}
                        onChange={(e) => setStress(e.target.value)}
                        required={true}
                      />
                    </div>
                    <div className="col-md-6 mt-3">
                      <Input
                        label={t("inputs.sleepQuality")}
                        type="select"
                        options={CALCULATOR_DATA.SLEEP_QUALITY}
                        value={sleepQuality}
                        onChange={(e) => setSleepQuality(e.target.value)}
                        required={true}
                      />
                    </div>
                  </div>

                  <div className="row mt-5">
                    <div
                      className="col-12 bg-white py-2 px-4 d-flex justify-content-between align-items-center"
                      style={{ borderRadius: "0.5rem" }}
                    >
                      <p className="grey-text mb-0">
                        {t("calculateBox.label")}
                      </p>
                      <Button
                        text={t("calculateBox.button")}
                        onClick={calculateSleep}
                      />
                    </div>
                  </div>
                  {location.state?.returnTo && result && (
                    <div className="text-center mt-3">
                      <Button
                        text={t("continueLearning")}
                        className="site-btn border-btn"
                        style={{ width: "100%", maxWidth: "200px" }}
                        onClick={() =>
                          navigate(location.state.returnTo, {
                            state: {
                              ...location.state,
                              resultKey: `${location.state.sectionIndex}-${location.state.lectureIndex}-Sleep`,
                              resultData: {
                                type: "Sleep",
                                result: `${result} hours`,
                                value: `${result} hours`,
                                timestamp: new Date().toISOString(),
                              },
                            },
                          })
                        }
                      />
                    </div>
                  )}
                </div>

                <div className="col-12 col-lg-4 mt-4 mt-lg-0">
                  <div
                    className="h-100 p-3 p-md-4 d-flex flex-column justify-content-center"
                    style={{
                      border: "1px solid white",
                      borderRadius: "1rem",
                      background: "rgba(112,112,112,0.4)",
                    }}
                  >
                    <h6>
                      {result
                        ? `${t("resultBox.calculated")} ${result} ${t(
                            "resultBox.calculated2",
                          )}`
                        : t("resultBox.title")}
                    </h6>
                    <p className="p-sm">{t("resultBox.description")}</p>
                    <div
                      className="bg-white w-100 p-3 text-center mt-3"
                      style={{
                        border: "0.5px solid gray",
                        height: "auto",
                        borderRadius: "0.5rem",
                      }}
                    >
                      <p className="m-0 text-black">
                        {t("resultBox.noteMain")}
                      </p>
                      <p className="m-0 grey-text p-sm">
                        {t("resultBox.noteSub")}
                      </p>
                      <Button text={t("resultBox.exploreTips")} to="/classes" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <FitnessToolsResultCard
              type="Sleep"
              result={
                result ? { result, timestamp: new Date().toISOString() } : null
              }
              historyData={historyData}
              showHistory={showHistory}
              toggleHistory={toggleHistory}
              handleSaveResult={handleSaveResult}
              handleDeleteLast={handleDeleteLast}
              formatHistoryEntry={formatHistoryEntry}
              formatDateTime={formatDateTime}
              chartData={SleepCartData?.data || []}
              period={period}
              handlePeriodChange={handlePeriodChange}
            />

            {(aiLoading || aiSuggestion) && (
              <div
                className={`ai-suggestion-box ${highlightAI ? "ai-highlight" : ""}`}
                ref={aiRef}
              >
                <h4 className="text-white mb-3">
                  {/* <FontAwesomeIcon icon={faRobot} color="var(--primaryColor)" />{" "} */}
                  <img
                    src="https://traineego.com/assets/images/favicon-2026.ico"
                    alt="AI Coach"
                    style={{
                      width: "45px",
                      marginRight: "8px",
                      borderRadius: "25px",
                    }}
                  />
                  {t("aiSuggestion.title")}
                </h4>

                {showSkeleton ? (
                  // <p style={{ color: "#aaa" }}>
                  //   <Spinner size="sm" /> {t("aiSuggestion.loading")}
                  // </p>
                  <AISkeleton />
                ) : (
                  <div
                    style={{ color: "#d1d1d6", lineHeight: "1.7" }}
                    dangerouslySetInnerHTML={{
                      __html: formatAISuggestion(aiSuggestion),
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      <OtherCalculatorSection
        data={CALCULATORS.filter(
          (item) => item.route !== window.location.pathname,
        )}
      />
      <Footer />
    </>
  );
};

export default Sleep;
